/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  signal
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NgSelectComponent} from '@ng-select/ng-select';
import {from, of, Subject, timer} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, take, takeUntil} from 'rxjs/operators';
import template from './tags-input.component.html';

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  if (value === '') {
    return true;
  }
  return defaultValue;
}

function coerceNumberAttr(value: any, defaultValue: number): number {
  if (typeof value === 'number') {
    return value;
  }

  const parsed = parseInt(`${value}`, 10);
  return Number.isFinite(parsed) ? parsed : defaultValue;
}

@Component({
  standalone: false,
  selector: 'tags-input',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TagsInputComponent),
    multi: true
  }]
})
export class TagsInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @HostBinding('class.adn-select-host') hostClass = true;
  @HostBinding('class.adn-select-host-multiple') multipleHostClass = true;
  @HostBinding('class.tags-input-host') tagsInputHostClass = true;

  @Input() placeholder = '';

  @Input() set minLength(value: number | string | undefined) {
    this._minLength = Math.max(0, coerceNumberAttr(value, 1));
  }
  get minLength(): number {
    return this._minLength;
  }

  @Input() set disabled(value: boolean | string | undefined) {
    this.disabledInput.set(coerceBooleanAttr(value));
  }
  get disabled(): boolean {
    return this.disabledInput();
  }

  @Input() set addFromAutocompleteOnly(value: boolean | string | undefined) {
    this.addFromAutocompleteOnlyFlag.set(coerceBooleanAttr(value));
  }
  get addFromAutocompleteOnly(): boolean {
    return this.addFromAutocompleteOnlyFlag();
  }

  @Input() autocomplete?: (query: string) => Promise<string[]> | string[];

  @Output() tagClicked = new EventEmitter<string>();
  @Output() tagAdded = new EventEmitter<string>();
  @Output() tagRemoved = new EventEmitter<string>();

  @HostBinding('attr.disabled') get hostDisabledAttr(): '' | null {
    return this.disabledState() ? '' : null;
  }

  @ViewChild('ngSelect') ngSelect?: NgSelectComponent;

  readonly ngSelectTypeahead$ = new Subject<string>();
  readonly loading = signal(false);
  readonly value = signal<string[]>([]);
  readonly displayItems = signal<string[]>([]);
  readonly disabledState = computed(() => this.disabledInput() || this.cvaDisabled());
  readonly allowCustomTags = computed(() => !this.addFromAutocompleteOnlyFlag());

  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private readonly disabledInput = signal(false);
  private readonly cvaDisabled = signal(false);
  private readonly addFromAutocompleteOnlyFlag = signal(false);
  private suggestions: string[] = [];
  private initialAutocompleteSearchInFlight = false;
  private destroyed = false;
  private onChange: (value: string[]) => void = () => undefined;
  onTouched: () => void = () => undefined;

  private _minLength = 1;

  ngOnInit(): void {
    this.refreshDisplayItems();

    // Keep ng-select in typeahead mode so the dropdown can open before async results arrive.
    this.ngSelectTypeahead$.pipe(takeUntil(this.destroy$)).subscribe();

    this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((query) => this.loadSuggestions(query)),
        takeUntil(this.destroy$)
      )
      .subscribe((suggestions) => {
        this.suggestions = suggestions;
        this.loading.set(false);
        this.refreshDisplayItems();
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: any): void {
    this.value.set(this.normalizeTags(value));
    this.refreshDisplayItems();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  onSelectionChange(nextValue: any): void {
    const previousValue = this.value();
    const normalizedValue = this.normalizeTags(nextValue);
    const previousValueSet = new Set(previousValue);
    const normalizedValueSet = new Set(normalizedValue);

    this.value.set(normalizedValue);
    this.refreshDisplayItems();
    this.onChange(normalizedValue.slice());

    normalizedValue
      .filter((tag) => !previousValueSet.has(tag))
      .forEach((tag) => this.tagAdded.emit(tag));

    previousValue
      .filter((tag) => !normalizedValueSet.has(tag))
      .forEach((tag) => this.tagRemoved.emit(tag));

    if (normalizedValue.length > previousValue.length) {
      this.clearSearchText();
    }
  }

  onSearch(event: {term?: string}): void {
    if (!this.autocomplete) {
      return;
    }

    const term = (event && event.term) || '';
    if (!term) {
      this.triggerEmptyAutocompleteSearch();
      return;
    }

    this.search$.next(term);
  }

  onOpen(): void {
    this.triggerEmptyAutocompleteSearch();
  }

  onFocus(): void {
    if (this.ngSelect && !this.ngSelect.isOpen) {
      timer(0).pipe(take(1)).subscribe(() => this.ngSelect?.open());
    }

    this.triggerEmptyAutocompleteSearch();
  }

  private triggerEmptyAutocompleteSearch(): void {
    if (!this.autocomplete || this.disabledState()) {
      return;
    }

    if (this.initialAutocompleteSearchInFlight) {
      return;
    }

    this.markLoading();
    this.initialAutocompleteSearchInFlight = true;
    this.loadSuggestions('', true)
      .pipe(take(1))
      .subscribe((suggestions) => {
        this.suggestions = suggestions;
        this.loading.set(false);
        this.initialAutocompleteSearchInFlight = false;
        this.refreshDisplayItems();
        this.ngSelect?.detectChanges();

        if (this.ngSelect && !this.ngSelect.isOpen) {
          this.ngSelect.open();
        }
      });
  }

  createTag = (term: string): string => this.normalizeTag(term);

  emitTagClick(tag: string, event: Event): void {
    event.stopPropagation();
    this.tagClicked.emit(tag);
  }

  formatTag(tag: string): string {
    return tag || '';
  }

  private loadSuggestions(query: string, ignoreMinLength = false) {
    const normalizedQuery = this.normalizeTag(query);
    if (!this.autocomplete || (!ignoreMinLength && normalizedQuery.length < this.minLength)) {
      this.loading.set(false);
      return of([]);
    }

    this.markLoading();

    return from(Promise.resolve(this.autocomplete(normalizedQuery) || []))
      .pipe(
        catchError(() => of([])),
        switchMap((suggestions) => of(this.normalizeTags(suggestions)))
      );
  }

  /** Defer loading spinner on — open/focus during CD was NG0100 on [loading]. */
  private markLoading(): void {
    // Macrotask: signal writes in a microtask re-enter synchronize (NG0103).
    setTimeout(() => {
      if (!this.destroyed) {
        this.loading.set(true);
      }
    }, 0);
  }

  private normalizeTags(value: any): string[] {
    const tags = Array.isArray(value) ? value : (value ? [value] : []);
    const normalizedTags: string[] = [];
    const seen = new Set<string>();

    tags.forEach((tag) => {
      const normalizedTag = this.normalizeTag(tag);
      if (!normalizedTag || seen.has(normalizedTag)) {
        return;
      }

      seen.add(normalizedTag);
      normalizedTags.push(normalizedTag);
    });

    return normalizedTags;
  }

  private normalizeTag(value: any): string {
    return `${value || ''}`.trim();
  }

  private refreshDisplayItems(): void {
    this.displayItems.set(this.normalizeTags([
      ...this.value(),
      ...this.suggestions
    ]));
  }

  private clearSearchText(): void {
    timer(0).pipe(take(1)).subscribe(() => {
      if (!this.ngSelect) {
        return;
      }

      this.ngSelect.filter('');
      this.suggestions = [];
      this.refreshDisplayItems();
    });
  }
}
